import { useEffect, useState, useRef } from "react";
import { Play, Pause, Plus, RotateCcw, Timer as TimerIcon } from "lucide-react";

function formatSeconds(secs) {
  const s = Math.max(0, Math.floor(secs));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function computeDisplay(task) {
  const stored = task.timer_remaining_seconds ?? (task.duration_minutes || 0) * 60;
  if (!task.timer_running || !task.timer_started_at) return stored;
  const elapsed = Math.floor((Date.now() - new Date(task.timer_started_at).getTime()) / 1000);
  return Math.max(0, stored - elapsed);
}

export default function TaskTimer({ task, onPatch }) {
  const [display, setDisplay] = useState(() => computeDisplay(task));
  const notifiedRef = useRef(false);

  // Live tick
  useEffect(() => {
    setDisplay(computeDisplay(task));
    notifiedRef.current = false;
    if (!task.timer_running) return;
    const interval = setInterval(() => {
      setDisplay(computeDisplay(task));
    }, 500);
    return () => clearInterval(interval);
  }, [task.timer_running, task.timer_started_at, task.timer_remaining_seconds, task.duration_minutes]);

  // Fire notification + auto-pause when reaches 0
  useEffect(() => {
    if (display <= 0 && task.timer_running && !notifiedRef.current) {
      notifiedRef.current = true;
      if ("Notification" in window && Notification.permission === "granted") {
        new Notification("Timer finished", { body: task.title });
      }
      onPatch({ timer_running: false, timer_started_at: null, timer_remaining_seconds: 0 });
    }
  }, [display, task.timer_running, task.title, onPatch]);

  const handleStart = () => {
    if (task.timer_running) {
      // pause: store current display
      onPatch({
        timer_running: false,
        timer_remaining_seconds: display,
        timer_started_at: null,
      });
    } else {
      // start: keep stored remaining, mark started
      onPatch({
        timer_running: true,
        timer_started_at: new Date().toISOString(),
      });
    }
  };

  const addSeconds = (secs) => {
    const stored = task.timer_remaining_seconds ?? (task.duration_minutes || 0) * 60;
    const newStored = stored + secs;
    onPatch({ timer_remaining_seconds: newStored });
  };

  const handleReset = () => {
    onPatch({
      timer_running: false,
      timer_started_at: null,
      timer_remaining_seconds: (task.duration_minutes || 0) * 60,
    });
  };

  const isDone = display <= 0;
  const isRunning = task.timer_running;

  return (
    <div
      className={`inline-flex items-center gap-1 border px-2 py-1 ${
        isDone
          ? "border-red-300 bg-red-50"
          : isRunning
          ? "border-[#002FA7] bg-blue-50"
          : "border-zinc-200 bg-white"
      }`}
      data-testid={`timer-${task.id}`}
    >
      <TimerIcon
        className={`w-3.5 h-3.5 ${isRunning ? "text-[#002FA7]" : isDone ? "text-red-600" : "text-zinc-500"}`}
        strokeWidth={1.5}
      />
      <span
        className={`font-mono text-sm tabular-nums ${
          isDone ? "text-red-700" : isRunning ? "text-[#002FA7] font-semibold" : "text-zinc-700"
        }`}
        data-testid={`timer-display-${task.id}`}
      >
        {formatSeconds(display)}
      </span>
      <button
        type="button"
        onClick={handleStart}
        className="p-1 text-zinc-600 hover:text-[#002FA7]"
        aria-label={isRunning ? "Pause timer" : "Start timer"}
        data-testid={`timer-toggle-${task.id}`}
      >
        {isRunning ? <Pause className="w-3 h-3" strokeWidth={2} /> : <Play className="w-3 h-3" strokeWidth={2} />}
      </button>
      <button
        type="button"
        onClick={() => addSeconds(60)}
        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 text-zinc-600 hover:text-[#002FA7] hover:bg-white border border-transparent hover:border-blue-200"
        aria-label="Add 1 minute"
        data-testid={`timer-add-min-${task.id}`}
      >
        +1m
      </button>
      <button
        type="button"
        onClick={() => addSeconds(30)}
        className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 text-zinc-600 hover:text-[#002FA7] hover:bg-white border border-transparent hover:border-blue-200"
        aria-label="Add 30 seconds"
        data-testid={`timer-add-sec-${task.id}`}
      >
        +30s
      </button>
      <button
        type="button"
        onClick={handleReset}
        className="p-1 text-zinc-400 hover:text-zinc-900"
        aria-label="Reset timer"
        data-testid={`timer-reset-${task.id}`}
      >
        <RotateCcw className="w-3 h-3" strokeWidth={1.5} />
      </button>
    </div>
  );
}
